/**
 * Database Service Module
 * Provides methods for querying notes and flashcards with:
 * - Connection management
 * - Error handling for connection failures and query timeouts
 * - Retry logic for transient failures
 */

import authService from './AuthService';
import PracticeSettingsService from './PracticeSettingsService';

// Base URL for API endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Configuration
const DEFAULT_TIMEOUT = 5000; // 5 seconds
const BULK_READ_TIMEOUT = 20000; // Large card collections can be slow on a cold database.
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

/**
 * Custom error class for database-related errors
 */
class DatabaseError extends Error {
  constructor(message, type = 'unknown', originalError = null) {
    super(message);
    this.name = 'DatabaseError';
    this.type = type; // 'connection', 'timeout', 'query', 'validation'
    this.originalError = originalError;
    this.timestamp = new Date();
  }
}

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getMostRecentEvaluation(card) {
  const assessmentGrade = PracticeSettingsService.getAssessmentGrade(card);
  if (!assessmentGrade) return null;

  return {
    grade: assessmentGrade,
    timestamp: card.lastPracticedAt || null,
  };
}

function isCompletedCard(card) {
  return PracticeSettingsService.isGraduated(card);
}

function getLastPracticedAt(card) {
  if (card.lastPracticedAt) {
    return new Date(card.lastPracticedAt);
  }
  return null;
}

function transformApiFlashCard(card) {
  const mostRecent = getMostRecentEvaluation(card);
  const lastRating = mostRecent?.grade ?? null;
  const lastReviewed = mostRecent?.timestamp ? new Date(mostRecent.timestamp) : null;

  return {
    id: card.id,
    noteId: card.noteId || 'default',
    noteDifficulty: card.noteDifficulty ?? card.note?.difficulty ?? null,
    front: card.front,
    back: card.back,
    memo: card.memo || null,
    contextPre: card.contextPre || null,
    contextAfter: card.contextAfter || null,
    backMemo: card.backMemo || null,
    ttsText: card.ttsText || null,
    readingTitle: card.readingTitle || null,
    readingContent: card.readingContent || null,
    readingSentences: Array.isArray(card.readingSentences) ? card.readingSentences : null,
    readingSegments: Array.isArray(card.readingSegments) ? card.readingSegments : null,
    readingCharacterNames: card.readingCharacterNames || null,
    dialogTitle: card.dialogTitle || null,
    dialogContent: card.dialogContent || null,
    dialogTurns: Array.isArray(card.dialogTurns) ? card.dialogTurns : null,
    dialogCharacterNames: card.dialogCharacterNames || null,
    isReadingGroup: Boolean(card.isReadingGroup),
    readingGroupId: card.readingGroupId || null,
    readingItemType: card.readingItemType || null,
    readingOrder: Number.isInteger(card.readingOrder) ? card.readingOrder : null,
    readingItemText: card.readingItemText || null,
    readingChildren: Array.isArray(card.readingChildren)
      ? card.readingChildren.map(transformApiFlashCard)
      : null,
    practiceAvailable: typeof card.practiceAvailable === 'boolean'
      ? card.practiceAvailable
      : null,
    sourceLevelUpCardId: card.sourceLevelUpCardId || null,
    levelUpUsed: Boolean(card.levelUpUsed),
    level: card.level,
    cardType: card.cardType,
    scoreLimit: card.scoreLimit,
    repeatsPerSession: card.repeatsPerSession,
    levelUpMethod: card.levelUpMethod,
    testIndex: card.testIndex || 0,
    graduationType: card.graduationType || null,
    completedAt: card.completedAt || null,
    question: card.front,
    answer: card.back,
    practiceCount: card.practiceCount || 0,
    score: card.score || 0,
    repeatCount: card.practiceCount || 0,
    lastPracticedAt: getLastPracticedAt(card),
    createdAt: card.createdAt || null,
    assessmentGrade: lastRating,
    lastReviewed,
    userId: card.userId,
  };
}

function getRequestHeaders(extraHeaders = {}) {
  const token = authService.getAuthToken?.();
  const userId = authService.getCurrentUserId();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : userId ? { 'x-user-id': userId } : {}),
    ...extraHeaders,
  };
}

/**
 * Fetch with timeout support
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>}
 * @throws {DatabaseError} On timeout or network error
 */
async function fetchWithTimeout(url, options = {}, timeout = DEFAULT_TIMEOUT) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  const requestHeaders = getRequestHeaders({ 'Content-Type': 'application/json', ...options.headers });

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: requestHeaders,
    });
    clearTimeout(timeoutId);
    if (response.status === 401) {
      authService.handleUnauthorized?.(requestHeaders.Authorization?.replace(/^Bearer\s+/i, ''));
    }
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Handle timeout
    if (error.name === 'AbortError') {
      throw new DatabaseError(
        `Query timeout: Request took longer than ${timeout}ms`,
        'timeout',
        error
      );
    }
    
    // Handle network errors
    if (error.name === 'TypeError' && (
      error.message.includes('fetch') ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.message.includes('NetworkError')
    )) {
      throw new DatabaseError(
        'Connection failure: Unable to connect to the database server',
        'connection',
        error
      );
    }
    
    // Re-throw other errors
    throw error;
  }
}

/**
 * Execute a database query with retry logic
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @param {number} retries - Number of retries remaining
 * @returns {Promise<any>} Response data
 * @throws {DatabaseError} On failure after all retries
 */
async function executeQuery(endpoint, options = {}, retries = MAX_RETRIES, timeout = DEFAULT_TIMEOUT) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetchWithTimeout(url, options, timeout);
    
    // Parse response body
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    // Handle HTTP errors
    if (!response.ok) {
      const errorMessage = data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`;
      throw new DatabaseError(
        errorMessage,
        'query',
        new Error(errorMessage)
      );
    }
    
    return data;
  } catch (error) {
    // If it's already a DatabaseError and we have retries left, retry for connection/timeout errors
    if (error instanceof DatabaseError && retries > 0 && 
        (error.type === 'connection' || error.type === 'timeout')) {
      await sleep(RETRY_DELAY);
      return executeQuery(endpoint, options, retries - 1, timeout);
    }
    
    // If it's a DatabaseError, re-throw it
    if (error instanceof DatabaseError) {
      throw error;
    }
    
    // Wrap other errors
    throw new DatabaseError(
      `Database query failed: ${error.message}`,
      'query',
      error
    );
  }
}

/**
 * Database Service class
 * Provides methods for accessing notes and flashcards
 */
class DatabaseService {
  constructor() {
    this.connected = false;
  }

  /**
   * Connect to the database
   * In a real implementation, this would establish a connection pool
   * For now, it just verifies the API is reachable
   * @returns {Promise<void>}
   * @throws {DatabaseError} On connection failure
   */
  async connect() {
    try {
      // Test connection by making a simple request
      await fetchWithTimeout(`${API_BASE_URL}/flashcards`, { method: 'GET' }, 3000);
      this.connected = true;
    } catch (error) {
      this.connected = false;
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        'Failed to connect to database',
        'connection',
        error
      );
    }
  }

  /**
   * Disconnect from the database
   * @returns {Promise<void>}
   */
  async disconnect() {
    this.connected = false;
  }

  /**
   * Check if connected to the database
   * @returns {boolean}
   */
  isConnected() {
    return this.connected;
  }

  /**
   * Get all notes for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of Note objects
   * @throws {DatabaseError} On query failure
   */
  async getAllNotes(userId, suppliedFlashcards = null, suppliedNotesMetadata = null) {
    if (!userId) {
      throw new DatabaseError(
        'User ID is required',
        'validation'
      );
    }

    try {
      const [notesMetadata, flashcards] = suppliedFlashcards && suppliedNotesMetadata
        ? [suppliedNotesMetadata, suppliedFlashcards]
        : await Promise.all([
            executeQuery('/notes'),
            executeQuery('/flashcards', {}, MAX_RETRIES, BULK_READ_TIMEOUT),
          ]);

      const noteFlashcards = new Map();
      const vocabularyNoteIds = new Set((notesMetadata || []).filter(note => note.studyKind === 'vocabulary').map(note => note.noteId || note.id));

      (notesMetadata || []).forEach(note => {
        if (note.studyKind === 'vocabulary') return;
        const noteId = note.noteId || note.id;
        if (!noteId) return;

        noteFlashcards.set(noteId, []);
      });

      flashcards.forEach(flashcard => {
        if (flashcard.studyKind === 'vocabulary' || vocabularyNoteIds.has(flashcard.noteId)) return;
        const noteId = flashcard.noteId || 'default';

        if (!noteFlashcards.has(noteId)) {
          noteFlashcards.set(noteId, []);
        }

        noteFlashcards.get(noteId).push(flashcard);
      });

      const metadataMap = new Map((notesMetadata || []).filter(note => note.studyKind !== 'vocabulary').map(note => [note.noteId || note.id, note]));

      return Array.from(noteFlashcards.entries()).map(([noteId, cards]) => {
        const metadata = metadataMap.get(noteId) || {};
        let gradeSum = 0;
        let gradeCount = 0;
        let practiceSum = 0;
        let completedCardCount = 0;

        cards.forEach(card => {
          practiceSum += card.practiceCount || 0;
          if (isCompletedCard(card)) {
            completedCardCount++;
          }
          const lastEval = getMostRecentEvaluation(card);
          if (lastEval?.grade) {
            gradeSum += lastEval.grade;
            gradeCount++;
          }
        });

        return {
          id: noteId,
          userId,
          title: metadata.noteTitle || `Note ${noteId}`,
          studyKind: metadata.studyKind || 'standard',
          isEnabled: metadata.isEnabled !== false,
          flashcardCount: cards.length,
          completedCardCount,
          coverUrl: metadata.coverUrl || null,
          sortOrder: metadata.sortOrder,
          createdAt: metadata.createdAt ? new Date(metadata.createdAt) : null,
          averageGrade: gradeCount > 0 ? gradeSum / gradeCount : 0,
          averagePracticeCount: cards.length > 0 ? practiceSum / cards.length : 0,
        };
      });
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to get notes: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Get a specific note by ID
   * @param {string} noteId - Note ID
   * @returns {Promise<Object|null>} Note object or null if not found
   * @throws {DatabaseError} On query failure
   */
  async getNoteById(noteId) {
    if (!noteId) {
      throw new DatabaseError(
        'Note ID is required',
        'validation'
      );
    }

    try {
      const metadata = await executeQuery(`/notes/${noteId}`);
      // Get all flashcards for this note
      const flashcards = await this.getFlashcardsByNote(noteId);
      
      if (flashcards.length === 0 && !metadata?.noteTitle) {
        return null;
      }
      
      const note = {
        id: noteId,
        userId: 'unknown',
        title: metadata?.noteTitle || `Note ${noteId}`,
        isEnabled: metadata?.isEnabled !== false,
        flashcardCount: flashcards.length,
        coverUrl: metadata?.coverUrl || null,
        createdAt: metadata?.createdAt ? new Date(metadata.createdAt) : null,
      };
      
      return note;
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to get note: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Get all flashcards for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of Flashcard objects with test metadata
   * @throws {DatabaseError} On query failure
   * 
   * Validates: Requirements 1.2, 4.5, 5.3, 7.1
   */
  async getAllFlashcards(userId, suppliedFlashcards = null) {
    if (!userId) {
      throw new DatabaseError(
        'User ID is required',
        'validation'
      );
    }

    try {
      const flashcards = suppliedFlashcards
        || await executeQuery('/flashcards', {}, MAX_RETRIES, BULK_READ_TIMEOUT);
      
      return flashcards.map(transformApiFlashCard);
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to get flashcards: ${error.message}`,
        'query',
        error
      );
    }
  }

  async getAvailablePracticeFlashcards(userId) {
    if (!userId) {
      throw new DatabaseError('User ID is required', 'validation');
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const flashcards = await executeQuery(
      `/flashcards?status=available&timezone=${encodeURIComponent(timezone)}`,
      {},
      MAX_RETRIES,
      BULK_READ_TIMEOUT
    );
    return this.getAllFlashcards(userId, flashcards);
  }

  /**
   * Load note metadata plus server-aggregated dashboard counters. Detailed card
   * content is intentionally deferred until a workout or editor needs it.
   * @param {string} userId - User ID
   * @returns {Promise<{notes: Array, dashboard: Object}>}
   */
  async getMainPageData(userId) {
    if (!userId) {
      throw new DatabaseError('User ID is required', 'validation');
    }

    const [notesMetadata, dashboard] = await Promise.all([
      executeQuery('/notes'),
      executeQuery('/flashcards/dashboard'),
    ]);

    const vocabularyIds = new Set((notesMetadata || []).filter(note => note.studyKind === 'vocabulary').map(note => note.noteId || note.id));
    const noteStats = new Map((dashboard?.notes || []).filter(note => !vocabularyIds.has(note.noteId) && note.studyKind !== 'vocabulary').map((note) => [note.noteId, note]));
    const metadata = new Map((notesMetadata || []).filter(note => note.studyKind !== 'vocabulary').map((note) => [note.noteId || note.id, note]));
    const noteIds = new Set([...metadata.keys(), ...noteStats.keys()]);
    const notes = Array.from(noteIds).map((noteId) => {
      const note = metadata.get(noteId) || {};
      const stats = noteStats.get(noteId) || {};
      return {
        id: noteId,
        userId,
        title: note.noteTitle || `Note ${noteId}`,
        studyKind: note.studyKind || 'standard',
        isEnabled: note.isEnabled !== false,
        flashcardCount: stats.flashcardCount || 0,
        completedCardCount: stats.completedCardCount || 0,
        coverUrl: note.coverUrl || null,
        sortOrder: note.sortOrder,
        createdAt: note.createdAt ? new Date(note.createdAt) : null,
        averageGrade: stats.averageGrade || 0,
        averagePracticeCount: stats.averagePracticeCount || 0,
      };
    });

    const enabledNotes = notes.filter((note) => note.isEnabled);
    const enabledNoteIds = new Set(enabledNotes.map((note) => note.id));
    const enabledStats = (dashboard?.notes || []).filter((note) => enabledNoteIds.has(note.noteId));

    return {
      notes,
      dashboard: {
        totalCardCount: enabledStats.reduce((total, note) => total + (note.flashcardCount || 0), 0),
        completedCardCount: enabledStats.reduce((total, note) => total + (note.completedCardCount || 0), 0),
        upgradeableCardCount: enabledStats.reduce((total, note) => total + (note.upgradeableCardCount || 0), 0),
        practiceSummaryProgress: enabledStats.every((note) => Number.isFinite(note.testingCardCount))
          ? enabledStats.reduce((counts, note) => ({
            practicing: counts.practicing + Math.max(0, (note.flashcardCount || 0) - (note.completedCardCount || 0) - note.testingCardCount),
            testing: counts.testing + note.testingCardCount,
          }), { practicing: 0, testing: 0 })
          : null,
      },
    };
  }

  /**
   * Get all flashcards for a specific note
   * @param {string} noteId - Note ID
   * @returns {Promise<Array>} Array of Flashcard objects with test metadata
   * @throws {DatabaseError} On query failure
   * 
   * Validates: Requirements 1.2, 4.5, 5.3, 7.1
   */
  async getFlashcardsByNote(noteId) {
    if (!noteId) {
      throw new DatabaseError(
        'Note ID is required',
        'validation'
      );
    }

    try {
      const scopedFlashcards = await executeQuery(
        `/flashcards?noteId=${encodeURIComponent(noteId)}`,
        {},
        MAX_RETRIES,
        BULK_READ_TIMEOUT
      );
      const noteFlashcards = scopedFlashcards.filter(card =>
        (card.noteId || 'default') === noteId
      );
      
      return noteFlashcards.map(transformApiFlashCard);
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to get flashcards for note: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Create a new flashcard
   * @param {Object} flashcardData - Flashcard data
   * @param {string} flashcardData.noteId - Parent note ID
   * @param {string} flashcardData.question - Question text
   * @param {string} flashcardData.answer - Answer text
   * @returns {Promise<Object>} Created flashcard with metadata
   * @throws {DatabaseError} On creation failure or validation error
   */
  async createFlashcard(flashcardData) {
    // Validate input
    if (!flashcardData || typeof flashcardData !== 'object') {
      throw new DatabaseError(
        'Flashcard data is required',
        'validation'
      );
    }

    if (!flashcardData.noteId) {
      throw new DatabaseError(
        'Note ID is required',
        'validation'
      );
    }

    if (!flashcardData.question || typeof flashcardData.question !== 'string' || flashcardData.question.trim() === '') {
      throw new DatabaseError(
        'Question is required and must be a non-empty string',
        'validation'
      );
    }

    if (!flashcardData.answer || typeof flashcardData.answer !== 'string' || flashcardData.answer.trim() === '') {
      throw new DatabaseError(
        'Answer is required and must be a non-empty string',
        'validation'
      );
    }

    try {
      // Map question/answer to front/back for backend API
      const requestBody = {
        front: flashcardData.question,
        back: flashcardData.answer,
        noteId: flashcardData.noteId
      };

      const response = await executeQuery('/flashcards', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      // Transform response to FlashcardWithMetadata format
      return {
        id: response.id,
        noteId: flashcardData.noteId,
        question: response.front,
        answer: response.back,
        lastTestTime: null,
        repeatCount: response.practiceCount || 0,
        score: response.score || 0,
        grade: response.assessmentGrade || null,
      };
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to create flashcard: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Delete multiple flashcards by ID
   * @param {string[]} flashcardIds - Array of flashcard IDs to delete
   * @returns {Promise<void>}
   * @throws {DatabaseError} On deletion failure or validation error
   */
  async deleteFlashcards(flashcardIds) {
    // Validate input
    if (!Array.isArray(flashcardIds)) {
      throw new DatabaseError(
        'Flashcard IDs must be an array',
        'validation'
      );
    }

    if (flashcardIds.length === 0) {
      throw new DatabaseError(
        'At least one flashcard ID is required',
        'validation'
      );
    }

    // Validate each ID
    for (const id of flashcardIds) {
      if (!id || typeof id !== 'string' || id.trim() === '') {
        throw new DatabaseError(
          'All flashcard IDs must be non-empty strings',
          'validation'
        );
      }
    }

    try {
      // Delete each flashcard individually
      // The backend doesn't have a batch delete endpoint, so we'll delete one by one
      const deletePromises = flashcardIds.map(id => 
        executeQuery(`/flashcards/${id}`, {
          method: 'DELETE'
        })
      );

      await Promise.all(deletePromises);
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to delete flashcards: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Delete a note and all its flashcards
   * @param {string} noteId - Note ID to delete
   * @returns {Promise<Object>} Deletion result with count
   * @throws {DatabaseError} On query failure or validation error
   */
  async deleteNote(noteId) {
    // Validate input
    if (!noteId || typeof noteId !== 'string' || noteId.trim() === '') {
      throw new DatabaseError(
        'Note ID must be a non-empty string',
        'validation'
      );
    }

    try {
      const result = await executeQuery(`/notes/${noteId}`, {
        method: 'DELETE'
      }, 0, BULK_READ_TIMEOUT);

      return result;
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to delete note: ${error.message}`,
        'query',
        error
      );
    }
  }


  /**
   * Get flashcards with metadata for a note
   * @param {string} noteId - Note ID
   * @returns {Promise<Array>} Array of flashcards with metadata
   * @throws {DatabaseError} On query failure or validation error
   */
  async getFlashcardsWithMetadata(noteId) {
    if (!noteId || typeof noteId !== 'string' || noteId.trim() === '') {
      throw new DatabaseError(
        'Note ID is required and must be a non-empty string',
        'validation'
      );
    }

    try {
      // Get all flashcards from the API
      const allFlashcards = await executeQuery('/flashcards', {}, MAX_RETRIES, BULK_READ_TIMEOUT);
      
      // Filter by noteId
      const noteFlashcards = allFlashcards.filter(card => 
        (card.noteId || 'default') === noteId
      );
      
      // Transform to FlashcardWithMetadata format
      return noteFlashcards.map(card => {
        // Get the most recent evaluation grade if available
        let lastEvaluation = null;
        let lastTestTime = null;
        
        const mostRecent = getMostRecentEvaluation(card);
        if (mostRecent) {
          lastEvaluation = mostRecent.grade;
          lastTestTime = mostRecent.timestamp ? new Date(mostRecent.timestamp) : null;
        }

        return {
          id: card.id,
          noteId: card.noteId || 'default',
          question: card.front,
          answer: card.back,
          lastTestTime: lastTestTime,
          repeatCount: card.practiceCount || 0,
          score: card.score || 0,
          grade: lastEvaluation,
        };
      });
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to get flashcards with metadata: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Get flashcard statistics (repeat count, last rating, last reviewed)
   * @param {string} flashcardId - Flashcard ID
   * @returns {Promise<Object>} FlashcardStats object
   * @throws {DatabaseError} On query failure or validation error
   * 
   * Validates: Requirements 5.3, 7.1
   */
  async getFlashcardStats(flashcardId) {
    if (!flashcardId || typeof flashcardId !== 'string' || flashcardId.trim() === '') {
      throw new DatabaseError(
        'Flashcard ID is required and must be a non-empty string',
        'validation'
      );
    }

    try {
      // Get all flashcards and find the specific one
      const allFlashcards = await executeQuery('/flashcards', {}, MAX_RETRIES, BULK_READ_TIMEOUT);
      const flashcard = allFlashcards.find(card => card.id === flashcardId);

      if (!flashcard) {
        throw new DatabaseError(
          `Flashcard with ID ${flashcardId} not found`,
          'query'
        );
      }

      // Get the most recent evaluation if available
      let lastRating = null;
      let lastReviewed = null;
      
      const mostRecent = getMostRecentEvaluation(flashcard);
      if (mostRecent) {
        lastRating = mostRecent.grade;
        lastReviewed = mostRecent.timestamp ? new Date(mostRecent.timestamp) : null;
      }

      return {
        flashcardId: flashcard.id,
        repeatCount: flashcard.practiceCount || 0,
        score: flashcard.score || 0,
        lastRating: lastRating,
        lastReviewed: lastReviewed
      };
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to get flashcard stats: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Save a difficulty rating for a flashcard
   * @param {string} flashcardId - Flashcard ID
   * @param {number} rating - Difficulty rating (1-5)
   * @param {Date} timestamp - When the rating was submitted
   * @returns {Promise<void>}
   * @throws {DatabaseError} On save failure or validation error
   * 
   * Validates: Requirements 4.5, 7.1
   */
  async saveRating(flashcardId, rating, timestamp) {
    // Validate inputs
    if (!flashcardId || typeof flashcardId !== 'string' || flashcardId.trim() === '') {
      throw new DatabaseError(
        'Flashcard ID is required and must be a non-empty string',
        'validation'
      );
    }

    if (typeof rating !== 'number' || !Number.isInteger(rating) || rating < 1 || rating > 5) {
      throw new DatabaseError(
        'Rating must be an integer between 1 and 5',
        'validation'
      );
    }

    if (!(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
      throw new DatabaseError(
        'Timestamp must be a valid Date object',
        'validation'
      );
    }

    try {
      // Save the rating via API
      await executeQuery(`/flashcards/${flashcardId}/evaluate`, {
        method: 'POST',
        body: JSON.stringify({
          grade: rating,
          timestamp: timestamp.toISOString()
        })
      });
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to save rating: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Increment the repeat counter for a flashcard
   * @param {string} flashcardId - Flashcard ID
   * @returns {Promise<void>}
   * @throws {DatabaseError} On update failure or validation error
   * 
   * Validates: Requirements 5.3, 7.2
   */
  async incrementRepeatCount(flashcardId) {
    if (!flashcardId || typeof flashcardId !== 'string' || flashcardId.trim() === '') {
      throw new DatabaseError(
        'Flashcard ID is required and must be a non-empty string',
        'validation'
      );
    }

    try {
      // Increment the practice count via API
      await executeQuery(`/flashcards/${flashcardId}/practice`, {
        method: 'POST'
      });
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to increment repeat count: ${error.message}`,
        'query',
        error
      );
    }
  }

  /**
   * Save session progress (batch operation for multiple ratings)
   * @param {Map<string, number>} ratings - Map of flashcardId to rating
   * @param {Date} timestamp - When the session progress was saved
   * @returns {Promise<void>}
   * @throws {DatabaseError} On save failure or validation error
   * 
   * Validates: Requirements 7.3
   */
  async saveSessionProgress(ratings, timestamp) {
    // Validate inputs
    if (!(ratings instanceof Map)) {
      throw new DatabaseError(
        'Ratings must be a Map',
        'validation'
      );
    }

    if (!(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
      throw new DatabaseError(
        'Timestamp must be a valid Date object',
        'validation'
      );
    }

    // Validate each rating
    for (const [flashcardId, rating] of ratings.entries()) {
      if (!flashcardId || typeof flashcardId !== 'string' || flashcardId.trim() === '') {
        throw new DatabaseError(
          'All flashcard IDs must be non-empty strings',
          'validation'
        );
      }

      if (typeof rating !== 'number' || !Number.isInteger(rating) || rating < 1 || rating > 5) {
        throw new DatabaseError(
          'All ratings must be integers between 1 and 5',
          'validation'
        );
      }
    }

    try {
      // Save each rating individually
      // The backend doesn't have a batch endpoint, so we'll save one by one
      const savePromises = Array.from(ratings.entries()).map(([flashcardId, rating]) =>
        this.saveRating(flashcardId, rating, timestamp)
      );

      await Promise.all(savePromises);
    } catch (error) {
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(
        `Failed to save session progress: ${error.message}`,
        'query',
        error
      );
    }
  }
}

// Export singleton instance
const databaseService = new DatabaseService();

export default databaseService;
export { DatabaseService, DatabaseError };
